import { BRAND_KEY_ONE_HUB_FOR_CARE } from "./brandConstants";
import { ROLE_TYPE_BUSINESS_VENDOR, ROLE_TYPE_CAREGIVER, ROLE_TYPE_FACILITY_WORKER, ROLE_TYPE_FAMILY_MEMBER, ROLE_TYPE_MHP, ROLE_TYPE_NURSE, ROLE_TYPE_PROVIDER } from "./RegisterConstants";

export type BillingBrandConfig = {
    brandKey?: string;
    brandName: string;
    providerLabel: string;
    providerPluralLabel: string;
    websiteLabel: string;
    vendorCommunityLabel: string;
};

export const DEFAULT_BILLING_BRAND_CONFIG: BillingBrandConfig = {
    brandKey: undefined,
    brandName: "AFH Hub",
    providerLabel: "AFH Provider",
    providerPluralLabel: "AFH Providers",
    websiteLabel: "AFH website",
    vendorCommunityLabel: "AFH community",
};

export class BillingConstants {
    static readonly PRICE_OPERATION = {
        ADD: "add",
        SUBTRACT: "subtract",
    }

    static readonly SINGLE_HOME_PLAN_RESIDENTS_PER_HOME = 3;
    static readonly SINGLE_HOME_PLAN_CAREGIVERS_PER_HOME = 5;
    static readonly PROVIDER_ADDITIONAL_CAREGIVER_BLOCK_SIZE = 1;
    static readonly NCP_CREDIT_PRICE = 20;
    static readonly ADMIN_TEST_CREDIT_PRICE = 1;
    static readonly ADMIN_TEST_SUBSCRIPTION_PRICE = 1;
    static readonly WEBSITE_PUBLISH_CREDIT_PRICE = 19;
    static readonly EXTRA_RESIDENT_UNLOCK_PRICE = 10;
    static readonly EXTRA_HOME_UNLOCK_PRICE = 15;
    static readonly FEATURE_CREDIT_TYPE = {
        NCP_GENERATION: "ncp_generation",
        ADMIN_TEST: "admin_test",
    } as const;

    static readonly SUBSCRIPTION_TYPE = {
        SINGLE_HOME: "single_home",
        ADD_RESIDENT: "add_resident",
        ADD_CAREGIVER: "add_caregiver",
        ADMIN_TEST: "admin_test",
        CAREGIVER_PLAN: "caregiver_plan",
        NURSE_BASE_PLAN: "nurse_base_plan",
        NURSE_AI_ASSESSMENT: "nurse_ai_assessment",
        FAMILY_PLAN: "family_plan",
        BUSINESS_VENDOR_PLAN: "business_vendor_plan",
        FACILITY_WORKER_FREE: "facility_worker_free",
    }

    static readonly PROVIDER_SINGLE_HOME_SUBSCRIPTION_PRICE = 29;
    static readonly PROVIDER_SINGLE_HOME_SUBSCRIPTION_PRICE_ONE_HUB_FOR_CARE = 59;
    static readonly PROVIDER_ADDITIONAL_RESIDENT_SUBSCRIPTION_PRICE = 5;
    static readonly PROVIDER_ADDITIONAL_CAREGIVER_SUBSCRIPTION_PRICE = 5;
    static readonly CAREGIVER_SUBSCRIPTION_PRICE = 19;
    static readonly FAMILY_MEMBER_SUBSCRIPTION_PRICE = 10;
    static readonly BUSINESS_VENDOR_SUBSCRIPTION_PRICE = 30;
    static readonly NURSE_BASE_SUBSCRIPTION_PRICE = 29;
    static readonly NURSE_INCLUDED_RESIDENTS = 5;
    static readonly NURSE_ADDITIONAL_RESIDENT_SUBSCRIPTION_PRICE = 5;
    // Deprecated legacy nurse automation constants kept temporarily so billing UI
    // can be simplified in stages without breaking builds.
    static readonly NURSE_INCLUDED_AI_ASSESSMENTS = 0;
    static readonly NURSE_AI_ASSESSMENT_BLOCK_SIZE = 0;
    static readonly NURSE_AI_ASSESSMENT_BLOCK_PRICE = 0;
    static readonly NURSE_MAX_AI_ASSESSMENTS = 0;
    static readonly PROVIDER_NURSE_AUTOMATION_PACK_PRICE = 5;
    static readonly PROVIDER_NURSE_AUTOMATION_PACK_SIZE = 1;
    static readonly NURSE_AI_ASSESSMENT_SUBSCRIPTION_PRICE = 0;

    static readonly PROVIDER_SERVICE_INCLUDE_SECTIONS = [
        {
            title: "Core access",
            items: [
                "Binder management",
                "Incident reporting",
                "Daily Notes",
                "eMAR",
                "ADL tracking with support levels, parameters, and daily reports",
                "Daily behavior tracking (CBHS/IBSS Tracker, monthly report generation)",
                "AFH website",
            ],
        },
        {
            title: "Automation tools",
            items: [
                "Negotiated care plan (NCP)",
                "Behavior and intervention care plan",
                "Preliminary service care plan",
                "Nurse assessment - For non-medicaid residents",
                "Nurse delegation form generation",
                "Assessment summarization",
                "Document comparison",
            ],
        },
        {
            title: "Staff Management",
            items: [
                "Add caregivers and RNs to your home",
                "Assign resident-by-resident staff access",
                "Permission-based access for eMAR, Daily Notes, incident reporting, and behavior tracking",
                "Clock-In / Clock-Out",
                "Pay calculator",
                "Worked hours tracking",
                "Overtime hours tracking",
                "Attendance and summary reports for compliance",
            ],
        },
        {
            title: "Billing terms",
            items: [
                "Cancel anytime",
                "No setup fee",
            ],
        },
    ] as const;

    static readonly NURSE_SERVICE_INCLUDE_SECTIONS = [
        {
            title: "Core access",
            items: [
                "Binder management",
                "ADL tracking and reports for authorized residents",
                "Delegation forms",
                "Podiatrist - Foot and Nail Care",
            ],
        },
        {
            title: "Resident support",
            items: [
                "Assessment drafting",
                "Care plan (NCP) drafting",
                "Delegation forms",
            ],
        },
        {
            title: "Billing terms",
            items: [
                "Cancel anytime",
                "No setup fee",
            ],
        },
    ] as const;

    static readonly CAREGIVER_SERVICES = [{
        name: "Subscription Plan for Caregiver",
        price: BillingConstants.CAREGIVER_SUBSCRIPTION_PRICE,
        required: true,
        includes: [
            "Includes core features such as reminders, document storage, ADL tracking, and care plan automation.",
        ],
        examples: [
            "If you are a caregiver supporting 1 home, you would need to purchase 1 Caregiver Plan subscription.",
            "If you are a caregiver supporting 2 homes, you would still only need to purchase 1 Caregiver Plan subscription.",
        ]
    }];

    static readonly NURSE_PLAN_SERVICE = {
        name: "Hub Plan",
        price: BillingConstants.NURSE_BASE_SUBSCRIPTION_PRICE,
        required: true,
        includes: BillingConstants.NURSE_SERVICE_INCLUDE_SECTIONS.flatMap((section) => section.items),
    };

    static readonly FACILITY_WORKER_FREE_SERVICE = {
        name: "Facility Worker Access",
        price: 0,
        required: false,
        free: true,
        paymentRequired: false,
        includes: [
            "Access facilities shared with you by a provider.",
            "View facility tasks assigned to you.",
            "Chart assigned facility tasks and review their status.",
            "Clock in and out for scheduled shifts.",
            "No subscription or payment method required.",
        ],
    };

    static readonly FAMILY_SERVICES = [{
        name: "Subscription Plan for Family Member",
        price: BillingConstants.FAMILY_MEMBER_SUBSCRIPTION_PRICE,
        required: true,
        includes: [
            "Document storage - Store important care documents such as care plans, medication lists, and appointment summaries in one secure place.",
        ],
    }];

    static getBusinessVendorServices(config: BillingBrandConfig = DEFAULT_BILLING_BRAND_CONFIG) {
        return [{
            name: "Subscription Plan for Business Vendor",
            price: BillingConstants.BUSINESS_VENDOR_SUBSCRIPTION_PRICE,
            required: true,
            includes: [
                "Business profile listing so providers, nurses, caregivers, and families can find your services.",
                `Connections workspace for building trusted ${config.brandName} relationships.`,
                "Ongoing access to your vendor dashboard and service categories.",
            ],
        }];
    }

    static getProviderSingleHomeSubscriptionPrice(config: BillingBrandConfig = DEFAULT_BILLING_BRAND_CONFIG) {
        return config.brandKey === BRAND_KEY_ONE_HUB_FOR_CARE
            ? BillingConstants.PROVIDER_SINGLE_HOME_SUBSCRIPTION_PRICE_ONE_HUB_FOR_CARE
            : BillingConstants.PROVIDER_SINGLE_HOME_SUBSCRIPTION_PRICE;
    }

    static getProviderAddOnConfig(config: BillingBrandConfig = DEFAULT_BILLING_BRAND_CONFIG) {
        return config.brandKey === BRAND_KEY_ONE_HUB_FOR_CARE
            ? { residentBlockSize: 5, residentBlockPrice: 20, staffBlockSize: 5, staffBlockPrice: 20 }
            : { residentBlockSize: 1, residentBlockPrice: BillingConstants.PROVIDER_ADDITIONAL_RESIDENT_SUBSCRIPTION_PRICE, staffBlockSize: BillingConstants.PROVIDER_ADDITIONAL_CAREGIVER_BLOCK_SIZE, staffBlockPrice: BillingConstants.PROVIDER_ADDITIONAL_CAREGIVER_SUBSCRIPTION_PRICE };
    }

    static getProviderServices(config: BillingBrandConfig = DEFAULT_BILLING_BRAND_CONFIG) {
        const addOnConfig = BillingConstants.getProviderAddOnConfig(config);
        const providerServiceSections = BillingConstants.PROVIDER_SERVICE_INCLUDE_SECTIONS.map((section) => ({
            ...section,
            items: section.items.map((item) => item === "AFH website" ? config.websiteLabel : item),
        }));

        return [{
            name: config.brandName,
            price: BillingConstants.getProviderSingleHomeSubscriptionPrice(config),
            required: true,
            includes: providerServiceSections.flatMap((section) => section.items),
            examples: [
                `If you have 1 home with 3 residents and 2 caregivers, one ${config.brandName} plan covers the whole setup.`,
                `If you need more capacity, add residents in blocks of ${addOnConfig.residentBlockSize} or staff in blocks of ${addOnConfig.staffBlockSize}.`,
            ]
        },
        {
            name: "Resident Add-On",
            price: addOnConfig.residentBlockPrice,
            includes: [
                `Add ${addOnConfig.residentBlockSize} additional resident slots to your ${config.brandName} subscription.`,
            ],
            examples: [
                `Each block of ${addOnConfig.residentBlockSize} residents adds $${addOnConfig.residentBlockPrice}/month beyond the 3 residents included in ${config.brandName}.`,
            ]
        },
        {
            name: "Staff Add-On",
            price: addOnConfig.staffBlockPrice,
            includes: [
                `Add ${addOnConfig.staffBlockSize} additional staff members to your ${config.brandName} subscription.`,
            ],
            examples: [
                `Each block of ${addOnConfig.staffBlockSize} staff adds $${addOnConfig.staffBlockPrice}/month beyond the ${BillingConstants.SINGLE_HOME_PLAN_CAREGIVERS_PER_HOME} staff included with each home.`,
            ]
        }];
    }

    static readonly BUSINESS_VENDOR_SERVICES = BillingConstants.getBusinessVendorServices();

    static readonly PROVIDER_SERVICES = BillingConstants.getProviderServices();

    static readonly ADMIN_TEST_SUBSCRIPTION_SERVICE = {
        name: "Admin Test Subscription",
        price: BillingConstants.ADMIN_TEST_SUBSCRIPTION_PRICE,
        required: false,
        includes: [
            "Admin-only recurring $1 subscription for validating subscription checkout and update flows.",
        ],
    };

    static getRoleTypeSubscriptionMapping(config: BillingBrandConfig = DEFAULT_BILLING_BRAND_CONFIG): Map<string, any> {
        return new Map([
            [ROLE_TYPE_PROVIDER, { services: BillingConstants.getProviderServices(config) }],
            [ROLE_TYPE_CAREGIVER, { services: BillingConstants.CAREGIVER_SERVICES }],
            [ROLE_TYPE_NURSE, { services: [BillingConstants.NURSE_PLAN_SERVICE] }],
            [ROLE_TYPE_MHP, { services: [BillingConstants.NURSE_PLAN_SERVICE] }],
            [ROLE_TYPE_FACILITY_WORKER, { services: [BillingConstants.FACILITY_WORKER_FREE_SERVICE] }],
            [ROLE_TYPE_FAMILY_MEMBER, { services: BillingConstants.FAMILY_SERVICES }],
            [ROLE_TYPE_BUSINESS_VENDOR, { services: BillingConstants.getBusinessVendorServices(config) }],
        ]);
    }

    static readonly ROLE_TYPE_SUBSCRIPTION_MAPPING: Map<string, any> = BillingConstants.getRoleTypeSubscriptionMapping();

    static readonly SUBSCRIPTION_PRICE_BY_TYPE: Record<string, number> = {
        [BillingConstants.SUBSCRIPTION_TYPE.SINGLE_HOME]: BillingConstants.PROVIDER_SINGLE_HOME_SUBSCRIPTION_PRICE,
        [BillingConstants.SUBSCRIPTION_TYPE.ADD_RESIDENT]: BillingConstants.PROVIDER_ADDITIONAL_RESIDENT_SUBSCRIPTION_PRICE,
        [BillingConstants.SUBSCRIPTION_TYPE.ADD_CAREGIVER]: BillingConstants.PROVIDER_ADDITIONAL_CAREGIVER_SUBSCRIPTION_PRICE,
        [BillingConstants.SUBSCRIPTION_TYPE.ADMIN_TEST]: BillingConstants.ADMIN_TEST_SUBSCRIPTION_PRICE,
        [BillingConstants.SUBSCRIPTION_TYPE.CAREGIVER_PLAN]: BillingConstants.CAREGIVER_SUBSCRIPTION_PRICE,
        [BillingConstants.SUBSCRIPTION_TYPE.NURSE_BASE_PLAN]: BillingConstants.NURSE_BASE_SUBSCRIPTION_PRICE,
        [BillingConstants.SUBSCRIPTION_TYPE.FAMILY_PLAN]: BillingConstants.FAMILY_MEMBER_SUBSCRIPTION_PRICE,
        [BillingConstants.SUBSCRIPTION_TYPE.BUSINESS_VENDOR_PLAN]: BillingConstants.BUSINESS_VENDOR_SUBSCRIPTION_PRICE,
        [BillingConstants.SUBSCRIPTION_TYPE.FACILITY_WORKER_FREE]: 0,
    };

    static readonly ROLE_SERVICE_TYPE_MAP: Record<string, string[]> = {
        [ROLE_TYPE_PROVIDER]: [
            BillingConstants.SUBSCRIPTION_TYPE.SINGLE_HOME,
            BillingConstants.SUBSCRIPTION_TYPE.ADD_RESIDENT,
            BillingConstants.SUBSCRIPTION_TYPE.ADD_CAREGIVER,
        ],
        [ROLE_TYPE_NURSE]: [
            BillingConstants.SUBSCRIPTION_TYPE.NURSE_BASE_PLAN,
        ],
        [ROLE_TYPE_MHP]: [
            BillingConstants.SUBSCRIPTION_TYPE.NURSE_BASE_PLAN,
        ],
        [ROLE_TYPE_FACILITY_WORKER]: [BillingConstants.SUBSCRIPTION_TYPE.FACILITY_WORKER_FREE],
        [ROLE_TYPE_CAREGIVER]: [BillingConstants.SUBSCRIPTION_TYPE.CAREGIVER_PLAN],
        [ROLE_TYPE_FAMILY_MEMBER]: [BillingConstants.SUBSCRIPTION_TYPE.FAMILY_PLAN],
        [ROLE_TYPE_BUSINESS_VENDOR]: [BillingConstants.SUBSCRIPTION_TYPE.BUSINESS_VENDOR_PLAN],
    };
}

export enum STRIPE_SUBSCRIPTION_STATUS {
    ACTIVE = "active",
    NOT_ACTIVE = "not_active"
}

export const FREE_TRIAL_ALLOWED_HOMES = 1;

const ADMIN_ROLE = "admin";

export const SHARED_BUNDLE_FILTER_OPTIONS = [
    { label: "All", value: "all" },
    { label: "Assessment", value: "assessment" },
    { label: "Care Plan", value: "careplan" },
    { label: "Resident", value: "resident" },
    { label: "Home", value: "home" },
] as const;

export const SHARED_BUNDLE_SORT_PRIORITY = ["assessment", "careplan", "resident", "home"] as const;
export const SHARED_ONE_TIME_BUNDLE_DISPLAY_PRIORITY = [
    "admin_test_bundle",
    "starter_ai_pack",
    "resident_care_bundle",
    "nurse_automation_pack",
] as const;

export const getSharedSubscriptionPlanContentByType = (config: BillingBrandConfig = DEFAULT_BILLING_BRAND_CONFIG): Record<string, {
    title: string;
    subtitle: string;
    description: string;
    benefits: string[];
    badgeLabel: string;
    isCorePlan: boolean;
    isAddOnPlan: boolean;
}> => ({
    [BillingConstants.SUBSCRIPTION_TYPE.SINGLE_HOME]: {
        title: config.brandName,
        subtitle: `1 home + ${BillingConstants.SINGLE_HOME_PLAN_RESIDENTS_PER_HOME} residents + ${BillingConstants.SINGLE_HOME_PLAN_CAREGIVERS_PER_HOME} caregivers included`,
        description: `Start here. ${config.brandName} covers 1 home, up to 3 residents, and up to 2 caregivers every month.`,
        benefits: [
            `Up to ${BillingConstants.SINGLE_HOME_PLAN_RESIDENTS_PER_HOME} residents included`,
            `Up to ${BillingConstants.SINGLE_HOME_PLAN_CAREGIVERS_PER_HOME} caregivers included`,
            `${config.websiteLabel} with unlimited updates`,
            "Binder management",
            "ADL tracking with support levels, parameters, and daily reports",
            "2 NCP automations per resident each month",
            "Unlimited assessment summarization",
            "Unlimited document comparison",
            "Behavior / intervention generation",
            "Preliminary care plan generation",
            "Cancel anytime",
            "No setup fee",
        ],
        badgeLabel: "Start Here",
        isCorePlan: true,
        isAddOnPlan: false,
    },
    [BillingConstants.SUBSCRIPTION_TYPE.ADD_RESIDENT]: {
        title: "Extra Resident",
        subtitle: `Only if you need more than ${BillingConstants.SINGLE_HOME_PLAN_RESIDENTS_PER_HOME} residents`,
        description: `Buy this only after the 3 included resident spots in ${config.brandName} are full. Each add-on adds 1 more resident.`,
        benefits: [
            `Adds 1 resident beyond the ${BillingConstants.SINGLE_HOME_PLAN_RESIDENTS_PER_HOME} included with ${config.brandName}`,
            "That resident also gets 2 NCP automations each month",
        ],
        badgeLabel: "Optional Add-On",
        isCorePlan: false,
        isAddOnPlan: true,
    },
    [BillingConstants.SUBSCRIPTION_TYPE.ADD_CAREGIVER]: {
        title: "Extra Caregiver",
        subtitle: `Only if you need more than ${BillingConstants.SINGLE_HOME_PLAN_CAREGIVERS_PER_HOME} caregivers`,
        description: `Buy this only after the ${BillingConstants.SINGLE_HOME_PLAN_CAREGIVERS_PER_HOME} included caregiver spots for a home are full. Each add-on adds ${BillingConstants.PROVIDER_ADDITIONAL_CAREGIVER_BLOCK_SIZE} more caregiver.`,
        benefits: [
            `Adds ${BillingConstants.PROVIDER_ADDITIONAL_CAREGIVER_BLOCK_SIZE} caregiver beyond the ${BillingConstants.SINGLE_HOME_PLAN_CAREGIVERS_PER_HOME} included with each ${config.brandName} home`,
            "Helps you add more caregiver access without changing the rest of your provider plan",
        ],
        badgeLabel: "Optional Add-On",
        isCorePlan: false,
        isAddOnPlan: true,
    },
    [BillingConstants.SUBSCRIPTION_TYPE.NURSE_BASE_PLAN]: {
        title: "Hub Plan",
        subtitle: `Includes ${BillingConstants.NURSE_INCLUDED_RESIDENTS} active residents`,
        description: `Use the shared hub workflow with ${BillingConstants.NURSE_INCLUDED_RESIDENTS} active residents included, plus binder management, delegation forms, and resident-scoped monthly AI support.`,
        benefits: [
            `${BillingConstants.NURSE_INCLUDED_RESIDENTS} active residents included`,
            "Binder management included",
            "Delegation forms included",
            "Podiatrist - Foot and Nail Care included",
            "2 assessment / delegation AI jobs per resident each month",
            `$${BillingConstants.NURSE_ADDITIONAL_RESIDENT_SUBSCRIPTION_PRICE}/month for each additional active resident`,
        ],
        badgeLabel: "Base Plan",
        isCorePlan: true,
        isAddOnPlan: false,
    },
    [BillingConstants.SUBSCRIPTION_TYPE.CAREGIVER_PLAN]: {
        title: "Caregiver Plan",
        subtitle: "Ongoing caregiver workflow access",
        description: "A straightforward monthly plan for caregivers who need ongoing access to everyday coordination and care tools.",
        benefits: [
            "Includes reminders for ongoing care coordination.",
            "Includes document storage for daily caregiver work.",
            "Includes ADL tracking with support levels, parameters, and daily reports.",
            "Includes care plan automation access within the caregiver workflow.",
        ],
        badgeLabel: "Best Place To Start",
        isCorePlan: true,
        isAddOnPlan: false,
    },
    [BillingConstants.SUBSCRIPTION_TYPE.FAMILY_PLAN]: {
        title: "Family Plan",
        subtitle: "Ongoing family access to resident information",
        description: "A simple monthly plan for family members who want reliable access to important resident information and documents.",
        benefits: [
            "Document storage for important resident information.",
            "Simple ongoing access for family review.",
            "Better fit than buying extra automation credits for regular check-ins.",
        ],
        badgeLabel: "Best Place To Start",
        isCorePlan: true,
        isAddOnPlan: false,
    },
    [BillingConstants.SUBSCRIPTION_TYPE.BUSINESS_VENDOR_PLAN]: {
        title: "Business Vendor Plan",
        subtitle: "Ongoing vendor presence and connection access",
        description: `A monthly plan for vendors who want to stay visible in ${config.brandName} and build ongoing relationships with providers, nurses, caregivers, and families.`,
        benefits: [
            "Business profile listing and service categories.",
            `Connections access for ongoing ${config.brandName} relationship building.`,
            "Vendor dashboard access for managing your offerings.",
        ],
        badgeLabel: "Best Place To Start",
        isCorePlan: true,
        isAddOnPlan: false,
    },
    [BillingConstants.SUBSCRIPTION_TYPE.FACILITY_WORKER_FREE]: {
        title: "Facility Worker Access",
        subtitle: "Free access to assigned facility work",
        description: "Facility Workers can use assigned facility task and shift tools without purchasing a subscription or adding a payment method.",
        benefits: BillingConstants.FACILITY_WORKER_FREE_SERVICE.includes,
        badgeLabel: "Free Access",
        isCorePlan: true,
        isAddOnPlan: false,
    },
    [BillingConstants.SUBSCRIPTION_TYPE.ADMIN_TEST]: {
        title: "Admin Test Subscription",
        subtitle: "Admin-only recurring $1 subscription",
        description: "Use this recurring $1 plan to validate admin subscription checkout, saved payment methods, and subscription update behavior.",
        benefits: BillingConstants.ADMIN_TEST_SUBSCRIPTION_SERVICE.includes,
        badgeLabel: "Admin Only",
        isCorePlan: false,
        isAddOnPlan: false,
    },
});

export const SHARED_SUBSCRIPTION_PLAN_CONTENT_BY_TYPE = getSharedSubscriptionPlanContentByType();

export const SHARED_ONE_TIME_BUNDLE_PACKAGES = [
    {
        name: "Admin Test Bundle",
        purchaseType: "admin_test_bundle",
        subtitle: "Admin-only $1 bundle for validating bundle checkout and balance updates.",
        price: 1,
        regularPrice: 3,
        includes: ["3 Admin Test Bundles"],
        regularPriceBreakdown: [
            { label: "3 Admin Test Bundles", amount: 3 },
        ],
        categories: ["assessment"],
        accentColor: "#7c3aed",
        background: "linear-gradient(135deg, #f5f3ff 0%, #ffffff 100%)",
        borderColor: "#c4b5fd",
        badge: "Admin Only",
    },
    {
        name: "Automation Credit",
        purchaseType: "nurse_automation_pack",
        subtitle: "Extra credits for automation jobs.",
        price: BillingConstants.PROVIDER_NURSE_AUTOMATION_PACK_PRICE,
        regularPrice: BillingConstants.PROVIDER_NURSE_AUTOMATION_PACK_PRICE,
        includes: [
            `${BillingConstants.PROVIDER_NURSE_AUTOMATION_PACK_SIZE} automation credits`,
            "1 credit can be used for either NCP or assessment",
        ],
        regularPriceBreakdown: [
            { label: `${BillingConstants.PROVIDER_NURSE_AUTOMATION_PACK_SIZE} Credits`, amount: BillingConstants.PROVIDER_NURSE_AUTOMATION_PACK_PRICE },
        ],
        categories: ["assessment"],
        accentColor: "#0f766e",
        background: "linear-gradient(135deg, #ecfeff 0%, #ffffff 100%)",
        borderColor: "#99f6e4",
    },
] as const;

export const SHARED_ROLE_ONE_TIME_BUNDLE_PURCHASE_TYPES: Record<string, string[]> = {
    [ADMIN_ROLE]: ["admin_test_bundle", "nurse_automation_pack"],
    [ROLE_TYPE_PROVIDER]: ["nurse_automation_pack"],
    [ROLE_TYPE_NURSE]: ["nurse_automation_pack"],
    [ROLE_TYPE_MHP]: ["nurse_automation_pack"],
    [ROLE_TYPE_CAREGIVER]: ["nurse_automation_pack"],
    [ROLE_TYPE_FAMILY_MEMBER]: ["nurse_automation_pack"],
    [ROLE_TYPE_BUSINESS_VENDOR]: ["nurse_automation_pack"],
};

export const SHARED_ONE_TIME_TAB_CONFIG_BY_ROLE: Record<string, {
    tabHeader: string;
    cardTitle: string;
    intro: string;
    bundleTitle: string;
    bundleDescription: string;
    balancesDescription: string;
    emptyState: string;
    showResidentBalance: boolean;
    showHomeBalance: boolean;
}> = {
    [ADMIN_ROLE]: {
        tabHeader: "Automation Credits",
        cardTitle: "Automation Credits",
        intro: "Buy automation credits whenever you need extra workflow capacity. Purchases are applied to your account right after successful payment.",
        bundleTitle: "Recommended Bundles",
        bundleDescription: "Choose a ready-made automation credit option when you need extra runs beyond your subscription.",
        balancesDescription: "Check your current residents, homes, website publishing access, and automation credit balances.",
        emptyState: "No automation credit options are available for this role yet.",
        showResidentBalance: true,
        showHomeBalance: false,
    },
    [ROLE_TYPE_PROVIDER]: {
        tabHeader: "Automation Credits",
        cardTitle: "Automation Credits",
        intro: "Buy automation credits whenever you need extra workflow capacity. Purchases are applied to your account right after successful payment.",
        bundleTitle: "Available Options",
        bundleDescription: "Choose an automation credit option when you need more runs without changing your subscription.",
        balancesDescription: "Check your current residents, homes, website publishing access, and automation credit balances.",
        emptyState: "No automation credit options are available for this role yet.",
        showResidentBalance: true,
        showHomeBalance: false,
    },
    [ROLE_TYPE_NURSE]: {
        tabHeader: "Automation Credits",
        cardTitle: "Automation Credits",
        intro: "Buy automation credits whenever you need extra workflow capacity. Purchases are applied to your account right after successful payment.",
        bundleTitle: "Available Options",
        bundleDescription: "Choose an automation credit option when you need more runs without changing your subscription.",
        balancesDescription: "Your available automation credits are shown here alongside any included monthly subscription usage.",
        emptyState: "No automation credit options are available for this role yet.",
        showResidentBalance: false,
        showHomeBalance: false,
    },
    [ROLE_TYPE_MHP]: {
        tabHeader: "Automation Credits",
        cardTitle: "Automation Credits",
        intro: "Buy automation credits whenever you need extra workflow capacity. Purchases are applied to your account right after successful payment.",
        bundleTitle: "Available Options",
        bundleDescription: "Choose an automation credit option when you need more runs without changing your subscription.",
        balancesDescription: "Your available automation credits are shown here alongside any included monthly subscription usage.",
        emptyState: "No automation credit options are available for this role yet.",
        showResidentBalance: false,
        showHomeBalance: false,
    },
    [ROLE_TYPE_CAREGIVER]: {
        tabHeader: "Automation Credits",
        cardTitle: "Automation Credits",
        intro: "Buy automation credits whenever you need extra workflow capacity. Purchases are applied to your account right after successful payment.",
        bundleTitle: "Available Options",
        bundleDescription: "Choose an automation credit option when you need more runs without changing your subscription.",
        balancesDescription: "Check any automation credit balances linked to your role.",
        emptyState: "No automation credit options are available for this role yet.",
        showResidentBalance: false,
        showHomeBalance: false,
    },
    [ROLE_TYPE_FAMILY_MEMBER]: {
        tabHeader: "Automation Credits",
        cardTitle: "Automation Credits",
        intro: "Buy automation credits whenever you need extra workflow capacity. Purchases are applied to your account right after successful payment.",
        bundleTitle: "Available Options",
        bundleDescription: "Choose an automation credit option when you need more runs without changing your subscription.",
        balancesDescription: "Check any automation credit balances linked to your role.",
        emptyState: "No automation credit options are available for this role yet.",
        showResidentBalance: false,
        showHomeBalance: false,
    },
    [ROLE_TYPE_BUSINESS_VENDOR]: {
        tabHeader: "Automation Credits",
        cardTitle: "Automation Credits",
        intro: "Buy automation credits whenever you need extra workflow capacity. Purchases are applied to your account right after successful payment.",
        bundleTitle: "Available Options",
        bundleDescription: "Choose an automation credit option when you need more runs without changing your subscription.",
        balancesDescription: "Check any automation credit balances linked to your role.",
        emptyState: "No automation credit options are available for this role yet.",
        showResidentBalance: false,
        showHomeBalance: false,
    },
};

export const getSharedPlanComparisonConfigByRole = (config: BillingBrandConfig = DEFAULT_BILLING_BRAND_CONFIG): Record<string, {
    dialogHeader: string;
    heroTitle: string;
    heroDescription: string;
    pricingTitle: string;
    pricingAmount: string;
    pricingDescription: string;
    pricingBullets: string[];
    includedTitle: string;
    includedDescription: string;
    includedColumns: [string, string];
    includedRows: Array<{ label: string; value: string }>;
    footerTitle: string;
    footerDescription: string;
    summary: string;
    leftTitle: string;
    leftDescription: string;
    leftBullets: string[];
    rightTitle: string;
    rightDescription: string;
    footer: string;
}> => ({
    [ROLE_TYPE_PROVIDER]: {
        dialogHeader: "Why Subscription Wins",
        heroTitle: "Best for ongoing provider work",
        heroDescription: "Subscription is the better choice when you are actively running a home and supporting residents month after month.",
        pricingTitle: "Predictable and ready to use",
        pricingAmount: `$${BillingConstants.getProviderServices(config)[0].price}/month`,
        pricingDescription: `${config.brandName} covers 1 home, up to ${BillingConstants.SINGLE_HOME_PLAN_RESIDENTS_PER_HOME} residents, and up to ${BillingConstants.SINGLE_HOME_PLAN_CAREGIVERS_PER_HOME} staff, with the core provider workflow already included. Staff - Caregivers and RNs you work with.`,
        pricingBullets: [
            `${config.websiteLabel} and unlimited website updates included.`,
            "Binder management included.",
            "2 NCP automations per resident each month.",
            "Unlimited assessment summarization and document comparison.",
            "Behavior / intervention and preliminary care plan generation included.",
            "Cancel anytime with no setup fee.",
        ],
        includedTitle: "What you get every month",
        includedDescription: "Your subscription keeps the core workflow available without needing to re-buy bundles as part of routine operations.",
        includedColumns: ["Included Monthly", "Per Resident"],
        includedRows: [
            { label: "Residents Included", value: `${BillingConstants.SINGLE_HOME_PLAN_RESIDENTS_PER_HOME}` },
            { label: "Staff Included", value: `${BillingConstants.SINGLE_HOME_PLAN_CAREGIVERS_PER_HOME} / home` },
            { label: "NCP Automations", value: "2 / resident / month" },
            { label: "Assessment Summarization", value: "Unlimited" },
            { label: "Document Comparison", value: "Unlimited" },
            { label: `${config.websiteLabel} Updates`, value: "Unlimited" },
            { label: "Binder Management", value: "Included" },
            { label: "Behavior / Intervention Generation", value: "Included" },
            { label: "Preliminary Care Plan Generation", value: "Included" },
        ],
        footerTitle: "Why it matters",
        footerDescription: "Subscription gives providers a smoother, lower-friction setup for ongoing monthly operations, especially when resident work, website updates, and documentation are part of the normal routine.",
        summary: "Subscription is the best fit for providers doing ongoing monthly care work across one home and multiple residents.",
        leftTitle: "Built for day-to-day operations",
        leftDescription: `${config.brandName} starts with 1 home, ${BillingConstants.SINGLE_HOME_PLAN_RESIDENTS_PER_HOME} included residents, and ${BillingConstants.SINGLE_HOME_PLAN_CAREGIVERS_PER_HOME} included staff, then grows with simple add-ons. Staff - Caregivers and RNs you work with.`,
        leftBullets: [
            `Predictable monthly billing at $${BillingConstants.getProviderServices(config)[0].price}/month.`,
            "$5 / month for each additional resident.",
            "$5 / month for each additional staff member.",
            "Unlimited website updates and binder management included.",
            "Best for teams doing ongoing care-plan and assessment work.",
        ],
        rightTitle: "Everything stays simple",
        rightDescription: "With subscription, your team does not need to keep track of separate bundle purchases every time routine work comes up.",
        footer: "Subscription is the smoother choice when you want steady monthly access, less billing friction, and a plan that supports ongoing provider workflows.",
    },
    [ROLE_TYPE_NURSE]: {
        dialogHeader: "Why Hub Plan Wins",
        heroTitle: "Simple monthly hub plan",
        heroDescription: "Keep billing easy with one monthly Hub Plan that includes core automation and day-to-day workflow access.",
        pricingTitle: "Monthly access",
        pricingAmount: "$29/month",
        pricingDescription: `The Hub Plan includes ${BillingConstants.NURSE_INCLUDED_RESIDENTS} active residents. The $29 base covers those ${BillingConstants.NURSE_INCLUDED_RESIDENTS} residents, then each additional active resident is $${BillingConstants.NURSE_ADDITIONAL_RESIDENT_SUBSCRIPTION_PRICE}/month.`,
        pricingBullets: [
            `Base price includes ${BillingConstants.NURSE_INCLUDED_RESIDENTS} active residents.`,
            `$${BillingConstants.NURSE_ADDITIONAL_RESIDENT_SUBSCRIPTION_PRICE}/month for each additional active resident.`,
            "Delegation forms automation is included in the subscription.",
            "Binder management is included.",
            "Podiatrist - Foot and Nail Care is included.",
            "Use the Automation Credit tab when you need extra automation.",
        ],
        includedTitle: "What you get every month",
        includedDescription: "Everything important stays in one simple plan.",
        includedColumns: ["Included Monthly", "Amount"],
        includedRows: [
            { label: "Assessment Automations", value: "5 / month" },
            { label: "NCP Automations", value: "5 / month" },
            { label: "Delegation Forms Automation", value: "Included" },
            { label: "Binder Management", value: "Included" },
            { label: "Podiatrist - Foot and Nail Care", value: "Included" },
            { label: "Manual Assessments", value: "Unlimited" },
        ],
        footerTitle: "Why it matters",
        footerDescription: "This keeps hub billing easier to understand and easier to manage month to month.",
        summary: "Subscription is the best fit for nurse-style users who want one simple Hub Plan with included workflow tools.",
        leftTitle: "Built for everyday hub work",
        leftDescription: `Use one Hub Plan for recurring assessment work, care plans, delegation forms, binder tasks, and ${BillingConstants.NURSE_INCLUDED_RESIDENTS} included active residents.`,
        leftBullets: [
            `Starts at $${BillingConstants.NURSE_BASE_SUBSCRIPTION_PRICE}/month for ${BillingConstants.NURSE_INCLUDED_RESIDENTS} active residents.`,
            `$${BillingConstants.NURSE_ADDITIONAL_RESIDENT_SUBSCRIPTION_PRICE}/month for each additional resident.`,
            "Delegation forms automation stays included.",
            "Binder management stays included.",
            "Podiatrist - Foot and Nail Care stays included.",
        ],
        rightTitle: "Everything stays simple",
        rightDescription: "When you need more automation beyond the included monthly amount, use the Automation Credit tab instead of changing the base subscription.",
        footer: "Subscription is the simpler choice when you want steady monthly access with one Hub Plan.",
    },
    [ROLE_TYPE_MHP]: {
        dialogHeader: "Why Hub Plan Wins",
        heroTitle: "Simple monthly hub plan",
        heroDescription: "Keep billing easy with the same monthly Hub Plan used for nurse-style workflow access.",
        pricingTitle: "Monthly access",
        pricingAmount: "$29/month",
        pricingDescription: `The Hub Plan includes ${BillingConstants.NURSE_INCLUDED_RESIDENTS} active residents. The $29 base covers those ${BillingConstants.NURSE_INCLUDED_RESIDENTS} residents, then each additional active resident is $${BillingConstants.NURSE_ADDITIONAL_RESIDENT_SUBSCRIPTION_PRICE}/month.`,
        pricingBullets: [
            `Base price includes ${BillingConstants.NURSE_INCLUDED_RESIDENTS} active residents.`,
            `$${BillingConstants.NURSE_ADDITIONAL_RESIDENT_SUBSCRIPTION_PRICE}/month for each additional active resident.`,
            "Uses the same included workflow model as nurse billing.",
            "Binder management is included.",
            "Use the Automation Credit tab when you need extra automation.",
        ],
        includedTitle: "What you get every month",
        includedDescription: "Everything important stays in one simple plan.",
        includedColumns: ["Included Monthly", "Amount"],
        includedRows: [
            { label: "Assessment Automations", value: "5 / month" },
            { label: "NCP Automations", value: "5 / month" },
            { label: "Binder Management", value: "Included" },
            { label: "Manual Assessments", value: "Unlimited" },
        ],
        footerTitle: "Why it matters",
        footerDescription: "This keeps hub billing easier to understand and easier to manage month to month.",
        summary: "Subscription is the best fit for MHP users who want the same simple Hub Plan structure as nurse billing.",
        leftTitle: "Built for everyday hub work",
        leftDescription: `Use one Hub Plan for recurring assessment work, care plans, binder tasks, and ${BillingConstants.NURSE_INCLUDED_RESIDENTS} included active residents.`,
        leftBullets: [
            `Starts at $${BillingConstants.NURSE_BASE_SUBSCRIPTION_PRICE}/month for ${BillingConstants.NURSE_INCLUDED_RESIDENTS} active residents.`,
            `$${BillingConstants.NURSE_ADDITIONAL_RESIDENT_SUBSCRIPTION_PRICE}/month for each additional resident.`,
            "Binder management stays included.",
            "Uses the same Hub Plan structure as nurse billing.",
        ],
        rightTitle: "Everything stays simple",
        rightDescription: "When you need more automation beyond the included monthly amount, use the Automation Credit tab instead of changing the base subscription.",
        footer: "Subscription is the simpler choice for steady MHP workflow each month.",
    },
    [ROLE_TYPE_CAREGIVER]: {
        dialogHeader: "Why Subscription Wins",
        heroTitle: "Best for steady caregiver access",
        heroDescription: "The caregiver subscription is the better option when you need ongoing access to daily tools instead of occasional extra purchases.",
        pricingTitle: "One plan, ongoing access",
        pricingAmount: `$${BillingConstants.CAREGIVER_SERVICES[0].price}/month`,
        pricingDescription: "The caregiver plan keeps your core workflow available every month without needing separate extra purchases.",
        pricingBullets: [
            "Simple recurring pricing.",
            "Keeps your day-to-day tools available.",
            "No need to rebuy access for routine work.",
            "Best fit for ongoing caregiver responsibilities.",
        ],
        includedTitle: "What you get every month",
        includedDescription: "Your caregiver subscription keeps the essential workflow available for everyday care tasks.",
        includedColumns: ["Included Monthly", "Access"],
        includedRows: [
            { label: "Reminders", value: "Included" },
            { label: "Document Storage", value: "Included" },
            { label: "Care Plan Automation Access", value: "Included" },
        ],
        footerTitle: "Why it matters",
        footerDescription: "Subscription gives caregivers uninterrupted access to the core tools they rely on during normal day-to-day work.",
        summary: "Subscription is the best fit for caregivers who need ongoing access to their everyday coordination tools.",
        leftTitle: "Built for everyday work",
        leftDescription: "Caregiver workflows are usually routine, so one monthly plan is simpler than waiting for extra purchases to cover normal work.",
        leftBullets: [
            `Predictable monthly billing at $${BillingConstants.CAREGIVER_SERVICES[0].price}/month.`,
            "Keeps reminders and documents available every month.",
            "No need to rebuy access for recurring work.",
        ],
        rightTitle: "Everything stays simple",
        rightDescription: "One plan keeps your main workflow active, which is easier for ongoing caregiver responsibilities.",
        footer: "Subscription is the clearer choice for caregiver work that continues month after month.",
    },
    [ROLE_TYPE_FAMILY_MEMBER]: {
        dialogHeader: "Why Subscription Wins",
        heroTitle: "Best for ongoing family access",
        heroDescription: "The family subscription makes more sense when you want reliable access to important resident information over time.",
        pricingTitle: "Low-cost monthly access",
        pricingAmount: `$${BillingConstants.FAMILY_SERVICES[0].price}/month`,
        pricingDescription: "The family plan keeps essential information available each month without needing separate purchases.",
        pricingBullets: [
            "Simple monthly cost.",
            "Reliable ongoing access to important information.",
            "No need to repurchase for routine viewing and organization.",
            "Best fit for family members who check in regularly.",
        ],
        includedTitle: "What you get every month",
        includedDescription: "Your family subscription keeps important resident information organized and available.",
        includedColumns: ["Included Monthly", "Access"],
        includedRows: [
            { label: "Document Storage", value: "Included" },
        ],
        footerTitle: "Why it matters",
        footerDescription: "Subscription gives family members a simple, ongoing way to stay connected to the documents and information they need.",
        summary: "Subscription is the best fit for family members who want steady access to resident documents and information.",
        leftTitle: "Built for ongoing access",
        leftDescription: "One monthly plan is simpler when you want information available over time instead of only once in a while.",
        leftBullets: [
            `Predictable monthly billing at $${BillingConstants.FAMILY_SERVICES[0].price}/month.`,
            "Keeps important resident information available.",
            "No extra purchasing decisions for routine access.",
        ],
        rightTitle: "Everything stays simple",
        rightDescription: "Family workflows are lighter, so a straightforward monthly plan works better than piecing together limited one-time access.",
        footer: "Subscription is the simpler option when you want reliable, low-friction access to important resident information.",
    },
    [ROLE_TYPE_BUSINESS_VENDOR]: {
        dialogHeader: "Why Subscription Wins",
        heroTitle: "Best for ongoing vendor visibility",
        heroDescription: `The business vendor subscription is the better fit when you want providers, nurses, caregivers, and families to keep finding your services on ${config.brandName}.`,
        pricingTitle: "Simple monthly vendor access",
        pricingAmount: `$${BillingConstants.getBusinessVendorServices(config)[0].price}/month`,
        pricingDescription: `The vendor plan keeps your business profile, service categories, and ${config.brandName} connection workflow available every month.`,
        pricingBullets: [
            "Predictable monthly vendor pricing.",
            `Keeps your business visible in ${config.brandName}.`,
            "Makes it easier to build ongoing trusted connections.",
            `Best fit for vendors serving the ${config.vendorCommunityLabel} regularly.`,
        ],
        includedTitle: "What you get every month",
        includedDescription: "Your business vendor subscription keeps your presence and relationship-building tools available without repurchasing access.",
        includedColumns: ["Included Monthly", "Access"],
        includedRows: [
            { label: "Business Profile Listing", value: "Included" },
            { label: "Service Categories", value: "Included" },
            { label: "Connections Workspace", value: "Included" },
        ],
        footerTitle: "Why it matters",
        footerDescription: `Subscription gives vendors a steady, professional presence inside ${config.brandName} so they can build relationships over time instead of relying on one-off access.`,
        summary: `Subscription is the best fit for vendors who want ongoing visibility and relationship building inside ${config.brandName}.`,
        leftTitle: "Built for ongoing vendor outreach",
        leftDescription: "Vendor work depends on staying visible and reachable over time, so one simple monthly plan is a better fit than occasional purchases.",
        leftBullets: [
            `Predictable monthly billing at $${BillingConstants.getBusinessVendorServices(config)[0].price}/month.`,
            "Keeps your vendor profile active every month.",
            `Supports steady relationship building with the ${config.vendorCommunityLabel}.`,
        ],
        rightTitle: "Everything stays simple",
        rightDescription: "One plan keeps your vendor presence active, which is easier than piecing together limited access when you want to be found consistently.",
        footer: `Subscription is the clearer choice when your business depends on staying visible and connected inside ${config.brandName}.`,
    },
});

export const SHARED_PLAN_COMPARISON_CONFIG_BY_ROLE = getSharedPlanComparisonConfigByRole();

